import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { formatDate } from '@/utils/date';
import { cn } from '@/utils/tailwind';

import Constants from '@/constants';

import {
  AchievementUnlocked,
  CertificateFooter,
  KeyCowIllustration as KeyCowIllustrationEnglish,
  KeyCowIllustrationSpanish,
  RedScriptLogo,
} from './components';
import { useDynamicNameSize } from './useDynamicNameSize';

interface CertificateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The name of the training plan that was completed. */
  planName: string;
  /** The full name of the team member receiving the certificate. */
  teamMemberName: string;
  /** The date when the plan was completed. */
  completionDate: string;
  /** The date when the certification expires. */
  expirationDate?: string;
  /** Pre-resolved plan description. */
  planDescription?: string;
  /** Total time spent completing the plan, in seconds. */
  completionDurationSeconds?: number;
  /** Specify the language for the certificate text. @default i18n.language */
  language?: 'en' | 'es' | (string & {});
  /** Callback fired when the certificate has finished rendering and sizing. */
  onReady?: () => void;
  /** Use fixed pixel units instead of container query units (for Firefox print compatibility). */
  forPrint?: boolean;
}

/** Certificate for a completed training plan. */
const Certificate: React.FC<CertificateProps> = ({
  planName,
  teamMemberName,
  completionDate,
  expirationDate,
  planDescription,
  completionDurationSeconds,
  language,
  onReady,
  forPrint = false,
  ...props
}) => {
  const { i18n } = useTranslation();
  const t = language ? i18n.getFixedT(language) : i18n.t;
  const activeLanguage = language ?? i18n.language;

  const { containerRef, fontSize, lineHeight, maxLines, isReady } =
    useDynamicNameSize(teamMemberName);

  useEffect(() => {
    if (isReady) {
      onReady?.();
    }
  }, [isReady, onReady]);

  const KeyCowIllustration =
    activeLanguage === Constants.LANGUAGE.SPANISH_LANGUAGE_CODE
      ? KeyCowIllustrationSpanish
      : KeyCowIllustrationEnglish;

  // Use fixed pixels for print mode (11in = 1056px at 96dpi, use percentage of that),
  // container query width units otherwise
  const u = forPrint ? 'px' : 'cqw';
  // Scale factor: convert cqw to px (1cqw = 1% of container = 10.56px for 1056px width)
  const s = forPrint ? 10.56 : 1;

  return (
    <div
      data-testid="Certificate"
      {...props}
      ref={containerRef}
      className={cn(
        'relative grid aspect-3300/2550 min-h-0 w-full grid-cols-[auto_1fr] overflow-hidden bg-white',
        !forPrint && '@container',
        props.className,
      )}
    >
      <AchievementUnlocked
        className="absolute h-auto"
        style={{
          top: `${1.5 * s}${u}`,
          right: `${1.5 * s}${u}`,
          width: `${7.8 * s}${u}`,
        }}
      />
      <KeyCowIllustration className="h-full w-auto" />
      <div
        className="flex h-full flex-col"
        style={{ padding: `${6.9 * s}${u} ${4 * s}${u} 0` }}
      >
        <RedScriptLogo className="h-auto" style={{ width: `${11 * s}${u}` }} />
        <p
          className="font-black tracking-wider wrap-break-word text-black uppercase"
          style={{
            paddingTop: `${2.1 * s}${u}`,
            fontSize: `${2.1 * s}${u}`,
            lineHeight: `${2.4 * s}${u}`,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {planName}
        </p>
        <div
          className="transition-opacity duration-150"
          style={{
            paddingTop: `${5.8 * s}${u}`,
            paddingBottom: `${5.3 * s}${u}`,
            maxWidth: `${47.64 * s}${u}`,
            opacity: isReady ? 1 : 0,
          }}
        >
          {/* Inline styles required for Firefox print compatibility */}
          <p
            className="font-[500] tracking-tighter wrap-break-word text-[#dd0033]"
            style={
              {
                fontSize: `${fontSize * s}${u}`,
                lineHeight: `${lineHeight * s}${u}`,
                paddingBottom: `${0.5 * s}${u}`,
                display: '-webkit-box',
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              } as React.CSSProperties
            }
          >
            {teamMemberName}
          </p>
        </div>
        <p
          className="text-black"
          style={{ fontSize: `${2.2 * s}${u}`, lineHeight: `${3.1 * s}${u}` }}
        >
          {t('certificates.successfullyCompleted')}{' '}
          <span className="font-[700]">
            {formatDate(completionDate, activeLanguage, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
          {expirationDate && (
            <>
              {' '}
              {t('certificates.andIsCertifiedUntil')}{' '}
              <span className="font-[700]">
                {formatDate(expirationDate, activeLanguage, {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </>
          )}
          .
        </p>
        <CertificateFooter
          planDescription={planDescription}
          completionDurationSeconds={completionDurationSeconds}
          language={language}
          forPrint={forPrint}
        />
      </div>
    </div>
  );
};

export default Certificate;
